import styled, { css } from 'styled-components';
import { COLOR } from '@src/styles/color';
import {
  BLOCK_VARIANT,
  CLIP_HEIGHT,
  CLIP_WAVEFORM_HEIGHT,
  TRIM_HANDLE_HIT_PX,
  TRIM_HANDLE_Z,
} from '@src/constants/timeline';

// Border precedence: an error clip is always red; otherwise a selected clip is
// navy; the rest use the neutral outline. Status (fill) and selection (border)
// are independent, so a trimmed clip can also be selected.
const borderFor = (variant, selected) => {
  if (variant === BLOCK_VARIANT.ERROR) return COLOR.functionalRed.default;
  if (selected) return COLOR.darkBlue;
  return COLOR.label[16];
};

// Fill per status: error → red tint, trimmed (has audio) → charcoal, else white.
const backgroundFor = (variant, selected) => {
  if (variant === BLOCK_VARIANT.ERROR) return COLOR.functionalRed[8];
  if (selected) return COLOR.charcoal;
  return COLOR.white;
};

// "No audio yet" fill — shown only when a clip has no waveform (block without
// playable audio, or an error clip); a clip with audio carries its real waveform
// instead. Two layers, matching the design's "illus" + "Blur":
//  - top layer: a white top-down fade so the stripes only show in the lower
//    ~half and dissolve behind the text at the top (white for the first 24%,
//    then out to transparent).
//  - bottom layer: 4.11px diagonal bars of content/black/4 at -45° (leaning the
//    opposite way to the design's illus), spaced on a ~8.49px perpendicular
//    period (12·sin45°).
const stripes = css`
  background-image: linear-gradient(
      to bottom,
      ${COLOR.white} 24%,
      rgba(255, 255, 255, 0) 100%
    ),
    repeating-linear-gradient(
      -45deg,
      ${COLOR.label[4]} 0 4.11px,
      transparent 4.11px 8.49px
    );
`;

export const StyledClip = styled.button`
  box-sizing: border-box;
  position: absolute;
  /* Centred by offset, NOT translateY(-50%): a transform makes the clip a
     stacking context, which would trap its trim handles' z-index inside the clip
     and leave them under the playhead line. The height is fixed, so the offset
     is exact and the layout is identical. */
  top: calc(50% - ${CLIP_HEIGHT / 2}px);
  height: ${CLIP_HEIGHT}px;
  display: flex;
  flex-direction: column;
  justify-content: start;
  gap: 2px;
  padding: 4px 0px;
  overflow: hidden;
  text-align: left;
  border: 0.5px solid ${({ variant, selected }) => borderFor(variant, selected)};
  border-radius: 8px;
  background-color: ${({ variant, selected }) =>
    backgroundFor(variant, selected)};
  ${({ hasWaveform, loading }) => !hasWaveform && !loading && stripes};
  /* Same rule the music clip uses, so both tracks feel identical: a clip is
     draggable whether or not it is selected, so it reads as an open hand
     throughout and closes for the drag. Playing (or converting / locked) it
     takes no click at all — don't offer an affordance for something that won't
     happen. */
  cursor: ${({ dragging, loading, isLocked, isPlaying }) => {
    if (loading || isLocked || isPlaying) return 'default';
    return dragging ? 'grabbing' : 'grab';
  }};
  touch-action: none;
  ${({ loading }) =>
    loading &&
    css`
      pointer-events: none;
      opacity: 0.5;
    `}
  /* Locked (a download/export is running): inert like a converting clip, but it
     keeps its normal look — the whole editor is frozen, not this one clip. */
  ${({ isLocked }) =>
    isLocked &&
    css`
      pointer-events: none;
    `}
`;

export const StyledClipText = styled.span`
  display: flex;
  align-items: center;
  gap: 2px;
  min-width: 0;
  font-size: 10px;
  padding: 0 4px;
  line-height: 16px;
  color: ${({ noContent }) => (noContent ? COLOR.label2[48] : COLOR.darkGray)};

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

// Leading glyph of a clip. A trimmed clip shows it as a filled navy pill with a
// white scissors icon; every other variant (default / error) shows a bare navy
// eq icon with no pill and a slightly larger glyph. `isTrimmed` toggles both the
// fill and the icon size/color.
export const StyledIconBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  padding: 2px;
  border-radius: 480px;
  background-color: ${({ isTrimmed }) =>
    isTrimmed ? COLOR.darkBlue : 'transparent'};

  svg {
    font-size: ${({ isTrimmed }) => (isTrimmed ? '10px' : '12px')};
    color: ${({ isTrimmed }) => (isTrimmed ? COLOR.white : COLOR.darkBlue)};
  }
`;

// Row (lane) holding the per-block audio waveform, below the text. Spans the
// clip width; the side margin lines the envelope up with the text's 4px inset.
export const StyledWaveformSlot = styled.span`
  position: relative;
  display: block;
  height: ${CLIP_WAVEFORM_HEIGHT}px;
  margin: 0 4px;
  overflow: hidden;
`;

// Left-anchored box the Waveform fills. Its width (set inline) is the audio's
// true span in ABSOLUTE px at the timeline's px-per-second — NOT a % of this
// margin-inset slot, whose inner width would make the px drift as the slot is
// trimmed. When the audio is longer than the slot (a trimmed clip) it overflows
// and the slot clips the tail — so a live trim-drag just moves the clip window
// over a pixel-stable envelope (no re-layout / drift).
export const StyledWaveformFill = styled.span`
  position: relative;
  display: block;
  height: 100%;
`;

// Stand-in for the envelope while a clip has playable audio but no peaks to draw
// — the clip is too narrow for the decode to be worth it (WAVEFORM_MIN_CLIP_PX),
// or its decode is still in flight. Without it the slot renders empty and, since
// `hasWaveform` already suppressed the "no audio yet" stripes, the clip reads as
// a blank white box. A flat baseline bar is what a silent passage looks like
// anyway (see MIN_AMP in Waveform), so it says "audio, just no detail here".
export const StyledWaveformBaseline = styled.span`
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 2px;
  transform: translateY(-50%);
  border-radius: 1px;
  background-color: ${COLOR.label[16]};
`;

// Grab zone for one trim edge on a selected clip — a full-height transparent
// strip hugging that edge. Width is min(TRIM_HANDLE_HIT_PX, 50%): a wide, easy
// target on a normal clip, but never wider than half the clip so the left and
// right zones meet at the midpoint instead of overlapping. That overlap is what
// made a narrow clip's left edge ungrabbable (the later-painted right zone won
// every pointer); tiling by side means whichever half you grab trims that edge.
//
// Sits ABOVE the playhead line (TRIM_HANDLE_Z), which parks on this very edge
// whenever the block is focused — its 12px grab strip would otherwise cover the
// whole visible trim bar and scrub instead of trimming. The playhead stays
// grabbable everywhere the handles are not, and the bar is the same navy as the
// line, so nothing looks different where they overlap.
export const StyledTrimHandle = styled.span`
  position: absolute;
  top: 0;
  bottom: 0;
  ${({ side }) => (side === 'right' ? 'right: 0;' : 'left: 0;')}
  width: min(${TRIM_HANDLE_HIT_PX}px, 50%);
  display: flex;
  align-items: center;
  justify-content: ${({ side }) =>
    side === 'right' ? 'flex-end' : 'flex-start'};
  padding: 0 1px;
  z-index: ${TRIM_HANDLE_Z};
  cursor: ew-resize;
  touch-action: none;
`;

// The visible bar inside a trim grab zone; non-interactive so the whole zone
// (not just the 2px bar) takes the pointer.
export const StyledTrimBar = styled.span`
  width: 2px;
  height: 20px;
  border-radius: 100px;
  background-color: ${COLOR.darkBlue};
  pointer-events: none;
`;
